import React, { useState, useEffect, useRef } from 'react';
import {
  IconChartBar, IconRefresh, IconArrowUpRight, IconArrowDownRight,
  IconTrendingUp, IconTrendingDown, IconTrash, IconStar, IconStarFilled,
} from '@tabler/icons-react';
import api from '../api/api';

const fmt = (v) => Number(v || 0).toLocaleString();
const fmtCap = (v) => {
  if (v == null) return '-';
  const n = Number(v);
  return n >= 10000 ? (n / 10000).toFixed(1) + '조' : fmt(n) + '억';
};
const fmtPbr = (v) => (v == null ? '-' : Number(v).toFixed(2));

/* 화면 너비에 따라 모바일 여부를 추적하는 훅 (팝업 그래프 반응형 처리용) */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 480);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
}
const getKSTDate = (monthsAgo = 0) => {
  const now = new Date();
  const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
  if (monthsAgo) kst.setMonth(kst.getMonth() - monthsAgo);
  return kst.toISOString().slice(0, 10);
};

/* 다크 테마에서도 잘 보이는 날짜 입력 스타일 (colorScheme: dark → 브라우저 기본 달력 아이콘도 밝게 표시) */
const dateInputStyle = (width) => ({
  width, background: '#1e2a44', color: '#f0f3fa', border: '1px solid #3d5578', colorScheme: 'dark',
});

/* ── API 호출 함수 ──────────────────────────────── */
const marketApi = {
  getMarketCapRanking: (date) => api.get('/stock/market-cap-ranking', { params: { date } }).then(r => r.data),
  getTrend: (startDate, endDate, r2, ratio) => api.get('/stock/market-cap-ranking/trend', { params: { startDate, endDate, r2, ratio } }).then(r => r.data),
  getRankTrend: (startDate, endDate, r2, ratio) => api.get('/stock/market-cap-ranking/rank-trend', { params: { startDate, endDate, r2, ratio } }).then(r => r.data),
  getTrendDefaults: () => api.get('/stock/market-cap-ranking/trend-defaults').then(r => r.data),
  getStockTrend: (query, startDate, endDate) => api.get('/stock/market-cap-ranking/stock-trend', { params: { query, startDate, endDate } }).then(r => r.data),
  getKrxMarketData: (date) => api.get('/krx/market-data', { params: { date } }).then(r => r.data),
  collectKrxStockDaily: (date) => api.post('/krx/stock-daily/collect', null, { params: { date } }).then(r => r.data),
  collectKrxInvestorTrading: (date) => api.post('/krx/investor-trading/collect', null, { params: { date } }).then(r => r.data),
  collectKrxStockValuation: (date) => api.post('/krx/stock-valuation/collect', null, { params: { date } }).then(r => r.data),
  collectKrxShortSelling: (date) => api.post('/krx/short-selling/collect', null, { params: { date } }).then(r => r.data),
  getKrxDataStatus: (startDate, endDate) => api.get('/krx/data-status', { params: { startDate, endDate } }).then(r => r.data),
  getScreeningDefaults: (date, market) => api.get('/krx/market-analysis/screening/defaults', { params: { date, market } }).then(r => r.data),
  getScreening: (params) => api.get('/krx/market-analysis/screening', { params }).then(r => r.data),
  getScreeningDetail: (date, market, stockCode, criteriaParams) => api.get('/krx/market-analysis/screening/detail', { params: { date, market, stockCode, ...(criteriaParams || {}) } }).then(r => r.data),
  deleteByDate: (date) => api.delete('/krx/data', { params: { date } }).then(r => r.data),
  getFavorites: () => api.get('/krx/data/favorites').then(r => r.data),
  addFavorite: (stockCode) => api.post('/krx/data/favorites', null, { params: { stockCode } }).then(r => r.data),
  removeFavorite: (stockCode) => api.delete('/krx/data/favorites', { params: { stockCode } }).then(r => r.data),
};

/* 다크 테마에서도 잘 보이는 숫자 입력 스타일 (R²/비율 조건 입력용) */
const numberInputStyle = (width) => ({
  width, background: '#1e2a44', color: '#f0f3fa', border: '1px solid #3d5578', colorScheme: 'dark',
});

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const fmtYmd = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/* [자산 > 시장분석] 시가총액 날짜피커 — krx_stock_daily/krx_investor_trading_daily/krx_stock_valuation_daily
   /krx_short_selling_daily/krx_etf_daily 5개 테이블의 데이터 존재여부를 날짜 셀에 점으로 표시하는 커스텀 달력.
   5개 테이블 모두 존재 = 초록 점, 1~4개만 존재 = 주황 점, 전혀 없음 = 표시 없음 */
function DataStatusDatePicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [viewDate, setViewDate] = useState(() => {
    const [y, m] = value.split('-').map(Number);
    return new Date(y, m - 1, 1);
  });
  const [statusMap, setStatusMap] = useState({});
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    if (!open) return;
    const monthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
    const monthEnd = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0);
    let alive = true;
    marketApi.getKrxDataStatus(fmtYmd(monthStart), fmtYmd(monthEnd))
      .then(res => { if (alive) setStatusMap(res || {}); })
      .catch(() => { if (alive) setStatusMap({}); });
    return () => { alive = false; };
  }, [open, viewDate]);

  const monthStart = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1);
  const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
  const startWeekday = monthStart.getDay();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const changeMonth = (delta) => setViewDate(v => new Date(v.getFullYear(), v.getMonth() + delta, 1));

  const selectDay = (d) => {
    onChange(fmtYmd(new Date(viewDate.getFullYear(), viewDate.getMonth(), d)));
    setOpen(false);
  };

  const dotColor = (count) => {
    if (count === 5) return '#2fb344';
    if (count > 0) return '#f59e0b';
    return null;
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className="form-control form-control-sm"
        style={{ ...dateInputStyle(150), textAlign: 'left', cursor: 'pointer' }}
        onClick={() => setOpen(o => !o)}
      >
        {value}
      </button>
      {open && (
        <div style={{
          position: 'absolute', top: '110%', right: 0, zIndex: 50,
          background: '#1e2a44', border: '1px solid #3d5578', borderRadius: 8,
          padding: 10, width: 240, boxShadow: '0 8px 24px rgba(0,0,0,.4)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <button type="button" onClick={() => changeMonth(-1)}
              style={{ background: 'none', border: 'none', color: '#f0f3fa', cursor: 'pointer', fontSize: 14, padding: '2px 8px' }}>‹</button>
            <div style={{ color: '#f0f3fa', fontSize: 13, fontWeight: 700 }}>
              {viewDate.getFullYear()}년 {viewDate.getMonth() + 1}월
            </div>
            <button type="button" onClick={() => changeMonth(1)}
              style={{ background: 'none', border: 'none', color: '#f0f3fa', cursor: 'pointer', fontSize: 14, padding: '2px 8px' }}>›</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
            {WEEKDAYS.map(w => (
              <div key={w} style={{ textAlign: 'center', color: '#7c8db0', fontSize: 10, padding: '2px 0' }}>{w}</div>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
            {cells.map((d, i) => {
              if (d == null) return <div key={i} />;
              const ymd = fmtYmd(new Date(viewDate.getFullYear(), viewDate.getMonth(), d));
              const isSelected = ymd === value;
              const color = dotColor(statusMap[ymd] || 0);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => selectDay(d)}
                  style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
                    padding: '4px 0', background: isSelected ? '#206bc4' : 'transparent',
                    border: 'none', borderRadius: 4, cursor: 'pointer',
                    color: isSelected ? '#fff' : '#e6e6e6', fontSize: 11,
                  }}
                >
                  <span>{d}</span>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: color || 'transparent' }} />
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── 코스피/코스닥 시가총액 TOP20 (주식장부 메뉴에서 이동) ─── */
const EMPTY_GROUP = { asOfDate: null, items: [] };

function MarketCapRankingBox({ date, setDate, reloadTick }) {
  const [data, setData] = useState({ kospi: EMPTY_GROUP, kosdaq: EMPTY_GROUP });
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState({ kospi: 10, kosdaq: 10 });
  const [detailStock, setDetailStock] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    marketApi.getMarketCapRanking(date)
      .then(res => { if (alive) setData(res || { kospi: EMPTY_GROUP, kosdaq: EMPTY_GROUP }); })
      .catch(() => { if (alive) setData({ kospi: EMPTY_GROUP, kosdaq: EMPTY_GROUP }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [date, reloadTick]);

  const renderChange = (item) => {
    if (item.isNew) return <span style={{ color: '#facc15', fontSize: 11, fontWeight: 700 }}>NEW</span>;
    if (item.rankChange == null || item.rankChange === 0) return <span style={{ color: '#666' }}>-</span>;
    if (item.rankChange > 0) {
      return <span style={{ color: '#e53e3e', display: 'inline-flex', alignItems: 'center', gap: 1 }}><IconArrowUpRight size={12} />{item.rankChange}</span>;
    }
    return <span style={{ color: '#3182ce', display: 'inline-flex', alignItems: 'center', gap: 1 }}><IconArrowDownRight size={12} />{Math.abs(item.rankChange)}</span>;
  };

  const renderTable = (items, market, asOfDate) => (
    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid #3d5578' }}>
          <th style={{ padding: '4px 4px', textAlign: 'left', color: '#7c8db0', fontWeight: 600, width: 45 }}>순위</th>
          <th style={{ padding: '4px 4px', textAlign: 'left', color: '#7c8db0', fontWeight: 600 }}>종목명</th>
          <th style={{ padding: '4px 4px', textAlign: 'right', color: '#7c8db0', fontWeight: 600, width: 36 }}>PBR</th>
          <th style={{ padding: '4px 4px', textAlign: 'right', color: '#7c8db0', fontWeight: 600 }}>시가총액</th>
          <th style={{ padding: '4px 4px', textAlign: 'right', color: '#7c8db0', fontWeight: 600, width: 46 }}>등락</th>
        </tr>
      </thead>
      <tbody>
        {items.map(item => (
          <tr key={item.stockCode} style={{ borderBottom: '1px solid #2a3a5a' }}>
            <td style={{ padding: '4px 4px', color: '#888', width: 45 }}>{item.rank}</td>
            <td style={{ padding: '4px 4px', color: '#e6e6e6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 100 }}>
              <button
                type="button"
                onClick={() => setDetailStock({ date: asOfDate, market, stockCode: item.stockCode, stockName: item.stockName })}
                style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit' }}
              >
                {item.stockName}
              </button>
            </td>
            <td style={{ padding: '4px 4px', textAlign: 'right', width: 36, color: (item.pbr != null && Number(item.pbr) <= 0.5) ? '#f87171' : '#ccc', fontWeight: (item.pbr != null && Number(item.pbr) <= 0.5) ? 700 : 400 }}>{fmtPbr(item.pbr)}</td>
            <td style={{ padding: '4px 4px', color: '#ccc', textAlign: 'right' }}>{fmtCap(item.marketCap)}</td>
            <td style={{ padding: '4px 4px', textAlign: 'right', width: 46 }}>{renderChange(item)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderGroup = (key, market, title, group) => {
    const items = group?.items || [];
    const isFallback = group?.asOfDate && group.asOfDate !== date;
    const maxLimit = Math.min(50, items.length);
    const limit = Math.min(expanded[key] || 10, maxLimit);
    const isMaxed = limit >= maxLimit;
    const visible = items.slice(0, limit);

    return (
      <div style={{ flex: '1 1 460px', minWidth: 300 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
          <div style={{ color: '#aaa', fontSize: 12, fontWeight: 600 }}>{title}</div>
          {group?.asOfDate && (
            <div style={{ color: isFallback ? '#facc15' : '#7c8db0', fontSize: 11 }}>
              {isFallback ? `(최근 데이터 : ${group.asOfDate} 기준)` : `(${group.asOfDate} 기준)`}
            </div>
          )}
          {limit > 10 && (
            <button
              className="btn btn-sm btn-outline-secondary"
              style={{ padding: '1px 8px', fontSize: 11, lineHeight: 1.6, marginLeft: 'auto' }}
              onClick={() => setExpanded(e => ({ ...e, [key]: 10 }))}
            >
              접기
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <div style={{ color: '#666', fontSize: 12, padding: '8px 0' }}>{loading ? '불러오는 중...' : '데이터 없음'}</div>
        ) : (
          <>
            {renderTable(visible, market, group?.asOfDate || date)}
            {!isMaxed && (
              <div style={{ textAlign: 'center', marginTop: 6 }}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  style={{ padding: '1px 8px', fontSize: 11, lineHeight: 1.6 }}
                  onClick={() => setExpanded(e => ({ ...e, [key]: Math.min(maxLimit, (e[key] || 10) + 10) }))}
                >
                  더보기 (+10)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="card mb-3" style={{ background: '#152238', border: 'none' }}>
      <div className="card-header" style={{ padding: '10px 14px', borderBottom: '1px solid #2a3a5a', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ color: '#e6e6e6', fontSize: 14, fontWeight: 700 }}>코스피/코스닥 시가총액 TOP20</div>
        <DataStatusDatePicker value={date} onChange={setDate} />
      </div>
      <div className="card-body py-3 px-3">
        <div className="d-flex gap-4 flex-wrap">
          {renderGroup('kospi', 'KOSPI', '코스피 시가총액 TOP20', data.kospi)}
          {renderGroup('kosdaq', 'KOSDAQ', '코스닥 시가총액 TOP20', data.kosdaq)}
        </div>
      </div>
      {detailStock && <ScreeningDetailModal stock={detailStock} onClose={() => setDetailStock(null)} />}
    </div>
  );
}

/* ── 미니 추세 스파크라인 ──────────────────────────
   valueKey: 값이 들어있는 필드명 (marketCap | rankNo)
   invert: true면 값이 작을수록 위로 그림 (순위는 숫자가 작을수록 좋으므로 사용) */
function Sparkline({ series, color, valueKey = 'marketCap', invert = false }) {
  const W = 120, H = 32, PAD = 2;
  if (!series || series.length < 2) return <svg width={W} height={H} />;

  const vals = series.map(p => Number(p[valueKey]) * (invert ? -1 : 1));
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;

  const points = series.map((p, i) => {
    const v = Number(p[valueKey]) * (invert ? -1 : 1);
    const x = PAD + (i / (series.length - 1)) * (W - PAD * 2);
    const y = H - PAD - ((v - min) / range) * (H - PAD * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}>
      <polyline points={points} fill="none" stroke={color} strokeWidth={1.5} />
    </svg>
  );
}

/* ── 추세 종목 리스트 (상승/하락 공통) ──────────────── */
function TrendList({ items, direction, date }) {
  const color = direction === 'RISING' ? '#e53e3e' : '#3182ce';
  const [selected, setSelected] = useState(null);
  const [detailStock, setDetailStock] = useState(null);

  if (!items || items.length === 0) {
    return <div style={{ color: '#666', fontSize: 12, padding: '12px 0' }}>조건을 만족하는 종목이 없습니다.</div>;
  }

  return (
    <>
    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ color: '#7c8db0', fontSize: 11 }}>
          <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>종목</th>
          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>등락률</th>
          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>R²</th>
          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>
            {direction === 'RISING' ? '상승일비율' : '하락일비율'}
          </th>
          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>추세</th>
        </tr>
      </thead>
      <tbody>
        {items.map(item => (
          <tr key={`${item.market}-${item.stockCode}`} style={{ borderBottom: '1px solid #2a3a5a' }}>
            <td style={{ padding: '5px 6px', color: '#e6e6e6' }}>
              <span style={{ color: '#7c8db0', fontSize: 10, marginRight: 4 }}>
                {item.market === 'KOSPI' ? '코스피' : '코스닥'}
              </span>
              <button
                type="button"
                onClick={() => setDetailStock({ date, market: item.market, stockCode: item.stockCode, stockName: item.stockName })}
                style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', fontWeight: 600 }}
              >
                {item.stockName}
              </button>
            </td>
            <td style={{ padding: '5px 6px', textAlign: 'right', color, fontWeight: 700 }}>
              {item.changePct >= 0 ? '+' : ''}{Number(item.changePct).toFixed(2)}%
            </td>
            <td style={{ padding: '5px 6px', textAlign: 'right', color: '#ccc' }}>{Number(item.r2).toFixed(2)}</td>
            <td style={{ padding: '5px 6px', textAlign: 'right', color: '#ccc' }}>
              {(Number(item.consistencyRatio) * 100).toFixed(0)}%
            </td>
            <td
              style={{ padding: '5px 6px', textAlign: 'right', cursor: 'pointer' }}
              title="클릭하면 상세 시가총액 그래프를 볼 수 있습니다"
              onClick={() => setSelected(item)}
            >
              <Sparkline series={item.series} color={color} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {selected && (
      <TrendChartModal
        item={selected}
        valueKey="marketCap"
        invert={false}
        headerText={`시가총액 추이 (${selected.changePct >= 0 ? '+' : ''}${Number(selected.changePct).toFixed(2)}%)`}
        color={color}
        onClose={() => setSelected(null)}
      />
    )}
    {detailStock && <ScreeningDetailModal stock={detailStock} onClose={() => setDetailStock(null)} />}
    </>
  );
}

/* ── 추이 상세 그래프 (레이어 팝업, 시가총액/순위 공통) ──────
   valueKey: 'marketCap' | 'rankNo'
   invert: true면 값이 작을수록 위로 그림 (순위용)
   headerText: 종목명 옆에 표시할 부가 설명 */
function TrendChartModal({ item, valueKey, invert, headerText, color, onClose }) {
  const isMobile = useIsMobile();
  const [activeIdx, setActiveIdx] = useState(null);
  const series = item.series || [];
  const W = isMobile ? 300 : 640;
  const H = isMobile ? 200 : 280;
  const PAD_L = isMobile ? 42 : 46, PAD_R = isMobile ? 10 : 16, PAD_T = isMobile ? 12 : 16, PAD_B = isMobile ? 26 : 34;
  const innerW = W - PAD_L - PAD_R, innerH = H - PAD_T - PAD_B;

  const vals = series.map(p => Number(p[valueKey]));
  const rawMin = Math.min(...vals), rawMax = Math.max(...vals);
  const range = rawMax - rawMin || 1;

  // invert=true(순위): 작은 값이 위로 / invert=false(시가총액): 큰 값이 위로
  const yFor = (v) => invert
    ? PAD_T + ((v - rawMin) / range) * innerH
    : PAD_T + ((rawMax - v) / range) * innerH;
  const xFor = (i) => PAD_L + (series.length <= 1 ? 0 : (i / (series.length - 1)) * innerW);
  const points = series.map((p, i) => `${xFor(i).toFixed(1)},${yFor(Number(p[valueKey])).toFixed(1)}`).join(' ');
  const labelStep = Math.max(1, Math.ceil(series.length / (isMobile ? 4 : 6)));
  const fmtAxis = (v) => invert ? `${v}위` : fmtCap(v);

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#152238', border: '1px solid #2a3a5a', borderRadius: 8, padding: isMobile ? 10 : 16, maxWidth: '92vw', maxHeight: '90vh', overflow: 'auto' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, gap: 8, flexWrap: 'wrap' }}>
          <div style={{ color: '#e6e6e6', fontSize: isMobile ? 12 : 14, fontWeight: 700 }}>
            <span style={{ color: '#7c8db0', fontSize: 11, marginRight: 6 }}>{item.market === 'KOSPI' ? '코스피' : '코스닥'}</span>
            {item.stockName} {headerText}
          </div>
          <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>닫기</button>
        </div>
        <svg width={W} height={H}>
          <line x1={PAD_L} y1={yFor(rawMin)} x2={W - PAD_R} y2={yFor(rawMin)} stroke="#2a3a5a" strokeDasharray="3,3" />
          <line x1={PAD_L} y1={yFor(rawMax)} x2={W - PAD_R} y2={yFor(rawMax)} stroke="#2a3a5a" strokeDasharray="3,3" />
          <text x={PAD_L - 6} y={yFor(rawMin) + 4} textAnchor="end" fontSize={isMobile ? 9 : 10} fill="#7c8db0">{fmtAxis(rawMin)}</text>
          <text x={PAD_L - 6} y={yFor(rawMax) + 4} textAnchor="end" fontSize={isMobile ? 9 : 10} fill="#7c8db0">{fmtAxis(rawMax)}</text>
          <polyline points={points} fill="none" stroke={color} strokeWidth={2} />
          {series.map((p, i) => (
            <circle
              key={p.date}
              cx={xFor(i)}
              cy={yFor(Number(p[valueKey]))}
              r={activeIdx === i ? (isMobile ? 4 : 5) : (isMobile ? 1.8 : 2.2)}
              fill={activeIdx === i ? '#0f1729' : color}
              stroke={color}
              strokeWidth={activeIdx === i ? 2 : 0}
              style={{ cursor: 'pointer' }}
              onClick={e => { e.stopPropagation(); setActiveIdx(prev => prev === i ? null : i); }}
            />
          ))}
          {series.map((p, i) => (
            (i % labelStep === 0 || i === series.length - 1) && (
              <text key={`x-${p.date}`} x={xFor(i)} y={H - PAD_B + 16} textAnchor="middle" fontSize={isMobile ? 8 : 9} fill="#7c8db0">
                {String(p.date).slice(5)}
              </text>
            )
          ))}
          {activeIdx != null && (() => {
            const p = series[activeIdx];
            const px = xFor(activeIdx), py = yFor(Number(p[valueKey]));
            const label = `${p.date}  ${fmtAxis(Number(p[valueKey]))}`;
            const boxW = label.length * (isMobile ? 5 : 6) + 12;
            const boxH = isMobile ? 16 : 18;
            let bx = px - boxW / 2;
            bx = Math.max(2, Math.min(bx, W - 2 - boxW));
            let by = py - boxH - 8;
            if (by < 0) by = py + 10;
            return (
              <g>
                <rect x={bx} y={by} width={boxW} height={boxH} rx={3} fill="#0f1729" stroke={color} strokeWidth={1} />
                <text x={bx + boxW / 2} y={by + boxH / 2 + 4} textAnchor="middle" fontSize={isMobile ? 9 : 10} fill="#f0f3fa">
                  {label}
                </text>
              </g>
            );
          })()}
        </svg>
      </div>
    </div>
  );
}

/* ── 순위 추세 종목 리스트 (순위상승/순위하락 공통) ─────── */
function RankTrendList({ items, direction, date }) {
  const color = direction === 'RISING' ? '#e53e3e' : '#3182ce';
  const [selected, setSelected] = useState(null);
  const [detailStock, setDetailStock] = useState(null);

  if (!items || items.length === 0) {
    return <div style={{ color: '#666', fontSize: 12, padding: '12px 0' }}>조건을 만족하는 종목이 없습니다.</div>;
  }

  return (
    <>
    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ color: '#7c8db0', fontSize: 11 }}>
          <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>종목</th>
          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>순위변화</th>
          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>R²</th>
          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>
            {direction === 'RISING' ? '순위상승일비율' : '순위하락일비율'}
          </th>
          <th style={{ textAlign: 'right', padding: '4px 6px', fontWeight: 600 }}>추세</th>
        </tr>
      </thead>
      <tbody>
        {items.map(item => (
          <tr key={`${item.market}-${item.stockCode}`} style={{ borderBottom: '1px solid #2a3a5a' }}>
            <td style={{ padding: '5px 6px', color: '#e6e6e6' }}>
              <span style={{ color: '#7c8db0', fontSize: 10, marginRight: 4 }}>
                {item.market === 'KOSPI' ? '코스피' : '코스닥'}
              </span>
              <button
                type="button"
                onClick={() => setDetailStock({ date, market: item.market, stockCode: item.stockCode, stockName: item.stockName })}
                style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', cursor: 'pointer', font: 'inherit', fontWeight: 600 }}
              >
                {item.stockName}
              </button>
            </td>
            <td style={{ padding: '5px 6px', textAlign: 'right', color, fontWeight: 700 }}>
              {item.firstRank}위 → {item.lastRank}위 ({item.rankChange > 0 ? '+' : ''}{item.rankChange})
            </td>
            <td style={{ padding: '5px 6px', textAlign: 'right', color: '#ccc' }}>{Number(item.r2).toFixed(2)}</td>
            <td style={{ padding: '5px 6px', textAlign: 'right', color: '#ccc' }}>
              {(Number(item.consistencyRatio) * 100).toFixed(0)}%
            </td>
            <td
              style={{ padding: '5px 6px', textAlign: 'right', cursor: 'pointer' }}
              title="클릭하면 상세 순위 그래프를 볼 수 있습니다"
              onClick={() => setSelected(item)}
            >
              <Sparkline series={item.series} color={color} valueKey="rankNo" invert />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    {selected && (
      <TrendChartModal
        item={selected}
        valueKey="rankNo"
        invert
        headerText={`순위 추이 (${selected.firstRank}위 → ${selected.lastRank}위)`}
        color={color}
        onClose={() => setSelected(null)}
      />
    )}
    {detailStock && <ScreeningDetailModal stock={detailStock} onClose={() => setDetailStock(null)} />}
    </>
  );
}

/* ── R²/비율 조건 입력 (공통) ──────────────────────── */
function ThresholdInputs({ criteria, setCriteria }) {
  return (
    <div className="d-flex align-items-center gap-1">
      <span style={{ color: '#7c8db0', fontSize: 11 }}>R²≥</span>
      <input
        type="number" step="0.05" min="0" max="1"
        className="form-control form-control-sm"
        style={numberInputStyle(62)}
        value={criteria.r2}
        placeholder="전체"
        onChange={e => setCriteria(c => ({ ...c, r2: e.target.value }))}
      />
      <span style={{ color: '#7c8db0', fontSize: 11, marginLeft: 4 }}>비율≥</span>
      <input
        type="number" step="5" min="0" max="100"
        className="form-control form-control-sm"
        style={numberInputStyle(62)}
        value={criteria.ratio}
        placeholder="전체"
        onChange={e => setCriteria(c => ({ ...c, ratio: e.target.value }))}
      />
      <span style={{ color: '#7c8db0', fontSize: 11 }}>%</span>
    </div>
  );
}

/* ── 종목명/종목코드 검색 (특정 종목의 기간 내 순위/시가총액 변화, 필터 조건과 무관하게 표시) ──
   mode: 'rank' → rankTrend 표시, 'marketCap' → marketCapTrend 표시 */
function StockTrendSearchBox({ range, mode }) {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [chartOpen, setChartOpen] = useState(false);

  const search = () => {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError('');
    setResult(null);
    marketApi.getStockTrend(q, range.start, range.end)
      .then(res => {
        const item = mode === 'rank' ? res.rankTrend : res.marketCapTrend;
        if (!item) {
          setError('해당 기간 동안 데이터가 부족해 변화를 계산할 수 없습니다.');
          return;
        }
        setResult(res);
      })
      .catch(() => setError('종목을 찾을 수 없습니다.'))
      .finally(() => setLoading(false));
  };

  const item = result ? (mode === 'rank' ? result.rankTrend : result.marketCapTrend) : null;
  const changeValue = item ? (mode === 'rank' ? item.rankChange : Number(item.changePct)) : 0;
  const color = changeValue > 0 ? '#e53e3e' : changeValue < 0 ? '#3182ce' : '#7c8db0';

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #2a3a5a' }}>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        <span style={{ color: '#7c8db0', fontSize: 12 }}>종목명/종목코드</span>
        <input
          type="text"
          className="form-control form-control-sm"
          style={{ width: 140, background: '#0d1830', color: '#e6e6e6', border: '1px solid #2a3a5a' }}
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') search(); }}
          placeholder="예: 삼성전자, 005930"
        />
        <button className="btn btn-sm btn-outline-secondary" onClick={search} disabled={loading || !query.trim()}>
          검색
        </button>
        {loading && <span style={{ color: '#7c8db0', fontSize: 12 }}>조회 중...</span>}
        {!loading && error && <span style={{ color: '#e53e3e', fontSize: 12 }}>{error}</span>}
      </div>
      {!loading && item && (
        <div className="d-flex align-items-center gap-3 flex-wrap" style={{ marginTop: 8 }}>
          <div style={{ color: '#e6e6e6', fontSize: 13, fontWeight: 700 }}>
            <span style={{ color: '#7c8db0', fontSize: 10, marginRight: 4 }}>
              {result.market === 'KOSPI' ? '코스피' : '코스닥'}
            </span>
            {result.stockName}
          </div>
          {mode === 'rank' ? (
            <div style={{ color, fontWeight: 700, fontSize: 13 }}>
              {item.firstRank}위 → {item.lastRank}위 ({item.rankChange > 0 ? '+' : ''}{item.rankChange})
            </div>
          ) : (
            <div style={{ color, fontWeight: 700, fontSize: 13 }}>
              {item.changePct >= 0 ? '+' : ''}{Number(item.changePct).toFixed(2)}%
            </div>
          )}
          <div style={{ color: '#ccc', fontSize: 12 }}>R² {Number(item.r2).toFixed(2)}</div>
          <div style={{ color: '#ccc', fontSize: 12 }}>상승일비율 {(Number(item.consistencyRatio) * 100).toFixed(0)}%</div>
          <div
            style={{ cursor: 'pointer' }}
            title="클릭하면 상세 그래프를 볼 수 있습니다"
            onClick={() => setChartOpen(true)}
          >
            <Sparkline series={item.series} color={color} valueKey={mode === 'rank' ? 'rankNo' : 'marketCap'} invert={mode === 'rank'} />
          </div>
        </div>
      )}
      {chartOpen && item && (
        <TrendChartModal
          item={item}
          valueKey={mode === 'rank' ? 'rankNo' : 'marketCap'}
          invert={mode === 'rank'}
          headerText={mode === 'rank'
            ? `순위 추이 (${item.firstRank}위 → ${item.lastRank}위)`
            : `시가총액 추이 (${item.changePct >= 0 ? '+' : ''}${Number(item.changePct).toFixed(2)}%)`}
          color={color}
          onClose={() => setChartOpen(false)}
        />
      )}
    </div>
  );
}

/* ── 꾸준히 순위 상승/하락중인 종목 분석 (rank_no 기준) ──── */
function RankTrendAnalysisBox() {
  const [range, setRange] = useState({ start: getKSTDate(1), end: getKSTDate() });
  const [criteria, setCriteria] = useState({ r2: '0.3', ratio: '55' });
  const [result, setResult] = useState({ rising: [], falling: [] });
  const [loading, setLoading] = useState(false);

  const load = (r = range, c = criteria) => {
    setLoading(true);
    const r2 = c.r2 === '' || c.r2 == null ? undefined : c.r2;
    const ratio = c.ratio === '' || c.ratio == null ? undefined : Number(c.ratio) / 100;
    marketApi.getRankTrend(r.start, r.end, r2, ratio)
      .then(res => setResult(res || { rising: [], falling: [] }))
      .catch(() => setResult({ rising: [], falling: [] }))
      .finally(() => setLoading(false));
  };

  /* 화면 최초 진입 시 1회만 AI(Gemini) 추천 기본값(시작일/종료일/R²/비율)을 불러와 자동 조회한다.
     React.StrictMode 개발 모드 2회 실행 방지를 위해 ref로 가드한다. */
  const defaultsLoadedRef = useRef(false);
  useEffect(() => {
    if (defaultsLoadedRef.current) return;
    defaultsLoadedRef.current = true;
    console.log('[Gemini] 순위 상승/하락중인 종목 추천 기본값 요청');
    marketApi.getTrendDefaults()
      .then(d => {
        console.log(`[Gemini] 순위 상승/하락중인 종목 추천 기본값 응답 (source=${d.source})`, d);
        const newRange = { start: d.startDate, end: d.endDate };
        const newCriteria = { r2: String(d.r2), ratio: String(Math.round(Number(d.ratio) * 100)) };
        setRange(newRange);
        setCriteria(newCriteria);
        load(newRange, newCriteria);
      })
      .catch(e => {
        console.warn('[Gemini] 순위 상승/하락중인 종목 추천 기본값 요청 실패, 기존 기본값으로 조회', e);
        load();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="card mb-3" style={{ background: '#152238', border: 'none' }}>
      <div className="card-header" style={{ padding: '10px 14px', borderBottom: '1px solid #2a3a5a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ color: '#e6e6e6', fontSize: 14, fontWeight: 700 }}>순위 상승/하락중인 종목</div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <input
            type="date"
            className="form-control form-control-sm"
            style={dateInputStyle(140)}
            value={range.start}
            onChange={e => setRange(r => ({ ...r, start: e.target.value }))}
          />
          <span style={{ color: '#7c8db0' }}>~</span>
          <input
            type="date"
            className="form-control form-control-sm"
            style={dateInputStyle(140)}
            value={range.end}
            onChange={e => setRange(r => ({ ...r, end: e.target.value }))}
          />
          <ThresholdInputs criteria={criteria} setCriteria={setCriteria} />
          <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={load} disabled={loading}>
            <IconRefresh size={14} className={loading ? 'rotating' : ''} />
            조회
          </button>
        </div>
      </div>
      <div className="card-body py-3 px-3">
        <div style={{ color: '#7c8db0', fontSize: 11, marginBottom: 10 }}>
          {criteria.r2 === '' && criteria.ratio === ''
            ? '시가총액이 아닌 순위(rank_no) 기준 추세입니다. R²/비율 조건 없이 기간 내 순위 등락폭 크기 순으로 정렬된 종목입니다.'
            : `시가총액이 아닌 순위(rank_no) 기준 추세입니다. 기간 내 TOP50에 충분히 머무른 종목 중 ${criteria.r2 !== '' ? `순위 회귀 추세(R² ≥ ${criteria.r2})` : ''}${criteria.r2 !== '' && criteria.ratio !== '' ? '와 ' : ''}${criteria.ratio !== '' ? `순위상승·하락일 비율(≥ ${criteria.ratio}%)` : ''}을 만족하는 종목만 표시됩니다.`}
        </div>
        <StockTrendSearchBox range={range} mode="rank" />
        <div className="d-flex gap-4 flex-wrap" style={{ marginTop: 12 }}>
          <div style={{ flex: '1 1 460px', minWidth: 320 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <IconTrendingUp size={16} color="#e53e3e" />
              <div style={{ color: '#e53e3e', fontSize: 13, fontWeight: 700 }}>순위 상승중인 종목 TOP10</div>
            </div>
            <RankTrendList items={result.rising} direction="RISING" date={range.end} />
          </div>
          <div style={{ flex: '1 1 460px', minWidth: 320 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <IconTrendingDown size={16} color="#3182ce" />
              <div style={{ color: '#3182ce', fontSize: 13, fontWeight: 700 }}>순위 하락중인 종목 TOP10</div>
            </div>
            <RankTrendList items={result.falling} direction="FALLING" date={range.end} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── 꾸준히 상승/하락중인 종목 분석 ─────────────────── */
function TrendAnalysisBox() {
  const [range, setRange] = useState({ start: getKSTDate(1), end: getKSTDate() });
  const [criteria, setCriteria] = useState({ r2: '0.3', ratio: '55' });
  const [result, setResult] = useState({ rising: [], falling: [] });
  const [loading, setLoading] = useState(false);

  const load = (r = range, c = criteria) => {
    setLoading(true);
    const r2 = c.r2 === '' || c.r2 == null ? undefined : c.r2;
    const ratio = c.ratio === '' || c.ratio == null ? undefined : Number(c.ratio) / 100;
    marketApi.getTrend(r.start, r.end, r2, ratio)
      .then(res => setResult(res || { rising: [], falling: [] }))
      .catch(() => setResult({ rising: [], falling: [] }))
      .finally(() => setLoading(false));
  };

  /* 화면 최초 진입 시 1회만 AI(Gemini) 추천 기본값(시작일/종료일/R²/비율)을 불러와 자동 조회한다.
     React.StrictMode 개발 모드 2회 실행 방지를 위해 ref로 가드한다. */
  const defaultsLoadedRef = useRef(false);
  useEffect(() => {
    if (defaultsLoadedRef.current) return;
    defaultsLoadedRef.current = true;
    console.log('[Gemini] 시가총액 상승/하락중인 종목 추천 기본값 요청');
    marketApi.getTrendDefaults()
      .then(d => {
        console.log(`[Gemini] 시가총액 상승/하락중인 종목 추천 기본값 응답 (source=${d.source})`, d);
        const newRange = { start: d.startDate, end: d.endDate };
        const newCriteria = { r2: String(d.r2), ratio: String(Math.round(Number(d.ratio) * 100)) };
        setRange(newRange);
        setCriteria(newCriteria);
        load(newRange, newCriteria);
      })
      .catch(e => {
        console.warn('[Gemini] 시가총액 상승/하락중인 종목 추천 기본값 요청 실패, 기존 기본값으로 조회', e);
        load();
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="card mb-3" style={{ background: '#152238', border: 'none' }}>
      <div className="card-header" style={{ padding: '10px 14px', borderBottom: '1px solid #2a3a5a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ color: '#e6e6e6', fontSize: 14, fontWeight: 700 }}>주가총액 상승/하락중인 종목</div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <input
            type="date"
            className="form-control form-control-sm"
            style={dateInputStyle(140)}
            value={range.start}
            onChange={e => setRange(r => ({ ...r, start: e.target.value }))}
          />
          <span style={{ color: '#7c8db0' }}>~</span>
          <input
            type="date"
            className="form-control form-control-sm"
            style={dateInputStyle(140)}
            value={range.end}
            onChange={e => setRange(r => ({ ...r, end: e.target.value }))}
          />
          <ThresholdInputs criteria={criteria} setCriteria={setCriteria} />
          <button className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1" onClick={load} disabled={loading}>
            <IconRefresh size={14} className={loading ? 'rotating' : ''} />
            조회
          </button>
        </div>
      </div>
      <div className="card-body py-3 px-3">
        <div style={{ color: '#7c8db0', fontSize: 11, marginBottom: 10 }}>
          {criteria.r2 === '' && criteria.ratio === ''
            ? 'R²/비율 조건 없이 기간 내 시가총액 등락폭 크기 순으로 정렬된 종목입니다.'
            : `기간 내 시가총액 TOP50에 충분히 머무른 종목 중 ${criteria.r2 !== '' ? `회귀 추세(R² ≥ ${criteria.r2})` : ''}${criteria.r2 !== '' && criteria.ratio !== '' ? '와 ' : ''}${criteria.ratio !== '' ? `상승·하락일 비율(≥ ${criteria.ratio}%)` : ''}을 만족하는 종목만 표시됩니다.`}
        </div>
        <StockTrendSearchBox range={range} mode="marketCap" />
        <div className="d-flex gap-4 flex-wrap" style={{ marginTop: 12 }}>
          <div style={{ flex: '1 1 460px', minWidth: 320 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <IconTrendingUp size={16} color="#e53e3e" />
              <div style={{ color: '#e53e3e', fontSize: 13, fontWeight: 700 }}>꾸준히 상승중인 종목 TOP10</div>
            </div>
            <TrendList items={result.rising} direction="RISING" date={range.end} />
          </div>
          <div style={{ flex: '1 1 460px', minWidth: 320 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
              <IconTrendingDown size={16} color="#3182ce" />
              <div style={{ color: '#3182ce', fontSize: 13, fontWeight: 700 }}>꾸준히 하락중인 종목 TOP10</div>
            </div>
            <TrendList items={result.falling} direction="FALLING" date={range.end} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── 조건검색 관련 단위 변환 헬퍼 ─────────────────────
   화면 입력/표시는 사람이 보기 편한 단위(억원)로 하되, API 요청 시에는 DB 원본 단위(원)로 환산해 보낸다. */
const eokToWon = (eok) => Math.round(Number(eok || 0) * 100_000_000);
const wonToEok = (won) => (won == null ? '' : String(Math.round(Number(won) / 100_000_000)));
const fmtWon = (v) => (v == null ? '-' : fmt(v) + '원');
const fmtPct = (v) => (v == null ? '-' : Number(v).toFixed(2) + '%');
const fmtWonCap = (v) => (v == null ? '-' : fmtCap(Number(v) / 100_000_000));
/* 순매수거래대금 등 부호가 의미 있는 금액에 명시적 +/- 부호를 붙여 표시 */
const fmtWonCapSigned = (v) => (v == null ? '-' : (Number(v) > 0 ? '+' : '') + fmtWonCap(v));
/* 수익률(%)에 명시적 +/- 부호를 붙여 표시 */
const fmtPctSigned = (v) => (v == null ? '-' : (Number(v) > 0 ? '+' : '') + Number(v).toFixed(2) + '%');

/* 전략 적합도(종합점수, 0~100) 구간별 배지 — 매수 추천이 아닌 "조건 충족도" 참고 지표임을 라벨로 명확히 함 */
const scoreBand = (score) => {
  const s = Number(score);
  if (s >= 80) return { label: '매우 높은 적합도', color: '#e53e3e' };
  if (s >= 60) return { label: '높은 적합도', color: '#f59f00' };
  if (s >= 40) return { label: '보통', color: '#7c8db0' };
  return { label: '낮은 적합도', color: '#4a5a7a' };
};
function ScoreBadge({ score }) {
  if (score == null) return <span style={{ color: '#666' }}>-</span>;
  const band = scoreBand(score);
  return (
    <span style={{ color: band.color, fontWeight: 700, fontSize: 12 }}>
      {Number(score).toFixed(1)}점 <span style={{ fontWeight: 500, fontSize: 11 }}>({band.label})</span>
    </span>
  );
}
/* 카테고리별 별점(0~5) 표시 */
function StarRating({ value }) {
  const v = Math.max(0, Math.min(5, Number(value || 0)));
  return <span style={{ color: '#f59f00', letterSpacing: 1 }}>{'★'.repeat(v)}{'☆'.repeat(5 - v)}</span>;
}

/* 조건검색 조건값 폼 상태 → API 요청 파라미터(DB 원본 단위) 변환 */
const criteriaToParams = (date, market, criteria, limit) => ({
  date, market,
  pbrMax: criteria.pbrMax,
  perMax: criteria.perMax,
  dividendYieldMin: criteria.dividendYieldMin,
  marketCapMin: eokToWon(criteria.marketCapMin),
  foreignNetBuyMin: eokToWon(criteria.foreignNetBuyMin),
  institutionNetBuyMin: eokToWon(criteria.institutionNetBuyMin),
  shortVolumeRatioMax: criteria.shortVolumeRatioMax,
  shortBalanceRatioMax: criteria.shortBalanceRatioMax,
  limit,
});

/* 조건검색 조건값 폼 상태 → 상세 레이어 "전략 적합도" 계산용 파라미터(DB 원본 단위, date/market/limit 제외) */
const criteriaToScoreParams = (criteria) => ({
  pbrMax: criteria.pbrMax,
  perMax: criteria.perMax,
  dividendYieldMin: criteria.dividendYieldMin,
  marketCapMin: eokToWon(criteria.marketCapMin),
  foreignNetBuyMin: eokToWon(criteria.foreignNetBuyMin),
  institutionNetBuyMin: eokToWon(criteria.institutionNetBuyMin),
  shortVolumeRatioMax: criteria.shortVolumeRatioMax,
  shortBalanceRatioMax: criteria.shortBalanceRatioMax,
});

/* AI(Gemini) 추천 기본값 응답(DB 원본 단위) → 화면 폼 상태(억원 단위 포함) 변환 */
const defaultsToCriteria = (d) => ({
  pbrMax: String(d.pbrMax), perMax: String(d.perMax), dividendYieldMin: String(d.dividendYieldMin),
  marketCapMin: wonToEok(d.marketCapMin),
  foreignNetBuyMin: wonToEok(d.foreignNetBuyMin),
  institutionNetBuyMin: wonToEok(d.institutionNetBuyMin),
  shortVolumeRatioMax: String(d.shortVolumeRatioMax), shortBalanceRatioMax: String(d.shortBalanceRatioMax),
});

/* ── 저평가 + 수급 + 공매도 조건검색 ────────────────── */
function ScreeningBox() {
  const isMobile = useIsMobile();
  const [date, setDate] = useState(getKSTDate());
  const [market, setMarket] = useState('ALL');
  const [criteria, setCriteria] = useState(null); // null이면 아직 AI 추천 기본값 로딩 전
  const [defaultsSource, setDefaultsSource] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [defaultsLoading, setDefaultsLoading] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);

  /* 화면 최초 진입 시 1회만 AI 추천 기본값을 불러온다. 이후 조건검색은 항상 화면에 입력된 현재 값으로 수행한다. */
  const loadDefaults = () => {
    setDefaultsLoading(true);
    console.log('[Gemini] 추천 기본값 요청', { date, market });
    marketApi.getScreeningDefaults(date, market)
      .then(d => {
        console.log(`[Gemini] 추천 기본값 응답 (source=${d.source})`, d);
        setCriteria(defaultsToCriteria(d));
        setDefaultsSource(d.source);
      })
      .catch((e) => {
        console.warn('[Gemini] 추천 기본값 요청 실패, fallback 사용', e);
        setCriteria({
          pbrMax: '1.5', perMax: '15', dividendYieldMin: '2.0',
          marketCapMin: '1000', foreignNetBuyMin: '10', institutionNetBuyMin: '10',
          shortVolumeRatioMax: '10', shortBalanceRatioMax: '5',
        });
        setDefaultsSource('FALLBACK');
      })
      .finally(() => setDefaultsLoading(false));
  };

  /* React.StrictMode 개발 모드에서 마운트 시 effect가 2번 실행되어 Gemini가 중복 호출되는 것을 방지 */
  const defaultsLoadedRef = useRef(false);
  useEffect(() => {
    if (defaultsLoadedRef.current) return;
    defaultsLoadedRef.current = true;
    loadDefaults();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const search = () => {
    if (!criteria) return;
    setLoading(true);
    marketApi.getScreening(criteriaToParams(date, market, criteria, 100))
      .then(res => setResult(res))
      .catch(e => {
        alert(`조건검색 실패: ${e.response?.data?.message || e.message}`);
        setResult(null);
      })
      .finally(() => setLoading(false));
  };

  /* AI 추천 기본값이 최초로 로드되면 자동으로 1회 조건검색을 실행한다 (버튼 클릭 없이 결과가 바로 보이도록). */
  const autoSearchedRef = useRef(false);
  useEffect(() => {
    if (criteria && !autoSearchedRef.current) {
      autoSearchedRef.current = true;
      search();
    }
  }, [criteria]); // eslint-disable-line react-hooks/exhaustive-deps

  const setC = (key) => (e) => setCriteria(c => ({ ...c, [key]: e.target.value }));

  return (
    <div className="card mb-3" style={{ background: '#152238', border: 'none' }}>
      <div className="card-header" style={{ padding: '10px 14px', borderBottom: '1px solid #2a3a5a', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ color: '#e6e6e6', fontSize: 14, fontWeight: 700 }}>저평가 + 수급 + 공매도 조건검색</div>
        <div className="d-flex align-items-center gap-2 flex-wrap">
          <input
            type="date"
            className="form-control form-control-sm"
            style={dateInputStyle(140)}
            value={date}
            onChange={e => setDate(e.target.value)}
          />
          <select
            className="form-select form-select-sm"
            style={{ ...numberInputStyle(100) }}
            value={market}
            onChange={e => setMarket(e.target.value)}
          >
            <option value="ALL">전체</option>
            <option value="KOSPI">코스피</option>
            <option value="KOSDAQ">코스닥</option>
          </select>
        </div>
      </div>
      <div className="card-body py-3 px-3">
        {!criteria ? (
          <div style={{ color: '#7c8db0', fontSize: 12 }}>{defaultsLoading ? 'AI 추천 조건값을 불러오는 중...' : '조건값을 불러오지 못했습니다.'}</div>
        ) : (
          <>
            <div style={{ color: '#7c8db0', fontSize: 11, marginBottom: 10 }}>
              기준일 최초 진입 시 표시되는 조건값은 AI(Gemini)가 당일 시장 데이터 분포를 참고해 추천한 값입니다
              {defaultsSource === 'FALLBACK' && ' (AI 추천 실패로 기본값 사용)'}. 값은 자유롭게 수정 후 검색할 수 있습니다.
            </div>
            <div className="d-flex flex-wrap gap-4 mb-3">
              <div>
                <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>저평가</div>
                <div className="d-flex align-items-center gap-1 flex-nowrap">
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11, cursor: 'help' }} title="주가순자산비율(PBR) - 낮을수록 순자산 대비 주가가 저평가">PBR≤</span>
                  <input type="number" step="0.1" className="form-control form-control-sm" style={numberInputStyle(isMobile ? 38 : 60)} value={criteria.pbrMax} onChange={setC('pbrMax')} />
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11, marginLeft: isMobile ? 3 : 6, cursor: 'help' }} title="주가수익비율(PER) - 낮을수록 순이익 대비 주가가 저평가">PER≤</span>
                  <input type="number" step="1" className="form-control form-control-sm" style={numberInputStyle(isMobile ? 38 : 60)} value={criteria.perMax} onChange={setC('perMax')} />
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11, marginLeft: isMobile ? 3 : 6, cursor: 'help' }} title="연간 배당금 ÷ 현재가(%) - 높을수록 배당 매력이 큼">{isMobile ? '배당≥' : '배당수익률≥'}</span>
                  <input type="number" step="0.1" className="form-control form-control-sm" style={numberInputStyle(isMobile ? 38 : 60)} value={criteria.dividendYieldMin} onChange={setC('dividendYieldMin')} />
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11 }}>%</span>
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11, marginLeft: isMobile ? 3 : 6, cursor: 'help' }} title="시가총액(억원) - 지나치게 작은 종목을 걸러내는 최소 기준">{isMobile ? '시총≥' : '시가총액≥'}</span>
                  <input type="number" step="100" className="form-control form-control-sm" style={numberInputStyle(isMobile ? 48 : 80)} value={criteria.marketCapMin} onChange={setC('marketCapMin')} />
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11 }}>{isMobile ? '억' : '억원'}</span>
                </div>
              </div>
              <div>
                <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>수급</div>
                <div className="d-flex align-items-center gap-1 flex-nowrap">
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11, cursor: 'help' }} title="외국인 투자자의 당일 순매수 거래대금(억원) - 클수록 외국인 매수세가 강함">{isMobile ? '외국인≥' : '외국인 순매수≥'}</span>
                  <input type="number" step="1" className="form-control form-control-sm" style={numberInputStyle(isMobile ? 44 : 70)} value={criteria.foreignNetBuyMin} onChange={setC('foreignNetBuyMin')} />
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11 }}>{isMobile ? '억' : '억원'}</span>
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11, marginLeft: isMobile ? 3 : 6, cursor: 'help' }} title="기관 투자자의 당일 순매수 거래대금(억원) - 클수록 기관 매수세가 강함">{isMobile ? '기관≥' : '기관 순매수≥'}</span>
                  <input type="number" step="1" className="form-control form-control-sm" style={numberInputStyle(isMobile ? 44 : 70)} value={criteria.institutionNetBuyMin} onChange={setC('institutionNetBuyMin')} />
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11 }}>{isMobile ? '억' : '억원'}</span>
                </div>
              </div>
              <div>
                <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 600, marginBottom: 4 }}>공매도</div>
                <div className="d-flex align-items-center gap-1 flex-nowrap">
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11, cursor: 'help' }} title="당일 전체 거래량 대비 공매도 거래량 비중(%) - 낮을수록 공매도 부담이 적음">거래비중≤</span>
                  <input type="number" step="0.5" className="form-control form-control-sm" style={numberInputStyle(isMobile ? 40 : 60)} value={criteria.shortVolumeRatioMax} onChange={setC('shortVolumeRatioMax')} />
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11 }}>%</span>
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11, marginLeft: isMobile ? 3 : 6, cursor: 'help' }} title="상장주식수 대비 공매도 잔고수량 비중(%) - 낮을수록 공매도 부담이 적음">잔고비중≤</span>
                  <input type="number" step="0.5" className="form-control form-control-sm" style={numberInputStyle(isMobile ? 40 : 60)} value={criteria.shortBalanceRatioMax} onChange={setC('shortBalanceRatioMax')} />
                  <span style={{ color: '#7c8db0', fontSize: isMobile ? 10 : 11 }}>%</span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                <button className="btn btn-sm btn-primary d-flex align-items-center gap-1" onClick={search} disabled={loading}>
                  <IconRefresh size={14} className={loading ? 'rotating' : ''} />
                  조건검색
                </button>
              </div>
            </div>

            {result && (
              <>
                <div style={{ color: '#e6e6e6', fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
                  검색 결과 {result.totalCount}종목{result.limited && ` (상위 ${result.items.length}건만 표시)`}
                  {result.date && result.date !== date && (
                    <span style={{ color: '#facc15', fontWeight: 500, marginLeft: 6 }}>
                      (최근 데이터 : {result.date} 기준)
                    </span>
                  )}
                </div>
                <ScreeningResultTable items={result.items} onSelect={(item) => setSelectedStock({ ...item, date: result.date, criteriaParams: criteriaToScoreParams(criteria) })} />
              </>
            )}
          </>
        )}
      </div>
      {selectedStock && (
        <ScreeningDetailModal
          stock={selectedStock}
          onClose={() => setSelectedStock(null)}
        />
      )}
    </div>
  );
}

/* ── 조건검색 결과 테이블 ────────────────────────── */
function ScreeningResultTable({ items, onSelect }) {
  if (!items || items.length === 0) {
    return <div style={{ color: '#666', fontSize: 12, padding: '12px 0' }}>조건을 만족하는 종목이 없습니다.</div>;
  }
  const th = { textAlign: 'right', padding: '4px 6px', fontWeight: 600 };
  return (
    <div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', minWidth: 720, fontSize: 12, borderCollapse: 'collapse', whiteSpace: 'nowrap' }}>
          <thead>
            <tr style={{ color: '#7c8db0', fontSize: 11 }}>
              <th style={{ textAlign: 'left', padding: '4px 6px', fontWeight: 600 }}>종목</th>
              <th style={th}>현재가</th>
              <th style={th}>시총</th>
              <th style={th}>PBR</th>
              <th style={th}>PER</th>
              <th style={th}>배당수익률</th>
              <th style={th}>외국인 순매수</th>
              <th style={th}>기관 순매수</th>
              <th style={th}>공매도 거래비중</th>
              <th style={th}>공매도 잔고비중</th>
              <th style={{ ...th, cursor: 'help' }} title="설정한 저평가·수급·공매도 조건을 종합한 참고 지표이며 매수 추천을 의미하지 않습니다.">전략 적합도</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr
                key={`${item.market}-${item.stockCode}`}
                style={{ borderBottom: '1px solid #2a3a5a', cursor: 'pointer' }}
                onClick={() => onSelect(item)}
              >
                <td style={{ padding: '5px 6px', color: '#e6e6e6' }}>
                  <span style={{ color: '#7c8db0', fontSize: 10, marginRight: 4 }}>
                    {item.market === 'KOSPI' ? '코스피' : '코스닥'}
                  </span>
                  <span style={{ fontWeight: 600 }}>{item.stockName}</span>
                </td>
                <td style={{ padding: '5px 6px', textAlign: 'right', color: '#ccc' }}>{fmt(item.closePrice)}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right', color: '#ccc' }}>{fmtWonCap(item.marketCap)}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right', color: '#ccc' }}>{fmtPbr(item.pbr)}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right', color: '#ccc' }}>{fmtPbr(item.per)}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right', color: '#ccc' }}>{fmtPct(item.dividendYield)}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right', color: '#e53e3e' }}>{fmtWonCapSigned(item.foreignNetBuyValue)}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right', color: '#3182ce' }}>{fmtWonCapSigned(item.institutionNetBuyValue)}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right', color: '#ccc' }}>{fmtPct(item.shortVolumeRatio)}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right', color: '#ccc' }}>{fmtPct(item.shortBalanceRatio)}</td>
                <td style={{ padding: '5px 6px', textAlign: 'right' }}><ScoreBadge score={item.score} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ color: '#5a6b8c', fontSize: 10, marginTop: 6 }}>
        전략 적합도는 설정한 저평가·수급·공매도 조건을 종합한 참고 지표이며 매수 추천을 의미하지 않습니다.
      </div>
    </div>
  );
}

/* ── 조건검색 결과 종목 클릭 시 상세 레이어 (TrendChartModal과 동일한 고정 오버레이 패턴) ── */
export function ScreeningDetailModal({ stock, onClose }) {
  const isMobile = useIsMobile();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favLoading, setFavLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    marketApi.getScreeningDetail(stock.date, stock.market, stock.stockCode, stock.criteriaParams)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  }, [stock]);

  /* 즐겨찾기는 [KRX 데이터] 화면과 동일한 계정별 종목 즐겨찾기(stock_code 기준)를 공유한다 */
  useEffect(() => {
    marketApi.getFavorites()
      .then(list => setIsFavorite((list || []).includes(stock.stockCode)))
      .catch(() => setIsFavorite(false));
  }, [stock]);

  const toggleFavorite = async () => {
    setFavLoading(true);
    try {
      if (isFavorite) {
        await marketApi.removeFavorite(stock.stockCode);
      } else {
        await marketApi.addFavorite(stock.stockCode);
      }
      setIsFavorite(v => !v);
    } catch (e) {
      alert(`즐겨찾기 처리 실패: ${e.response?.data?.message || e.message}`);
    } finally {
      setFavLoading(false);
    }
  };

  const row = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #1e2a44' }}>
      <span style={{ color: '#7c8db0', fontSize: 11 }}>{label}</span>
      <span style={{ color: '#e6e6e6', fontSize: 12, fontWeight: 600 }}>{value}</span>
    </div>
  );

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#152238', border: '1px solid #2a3a5a', borderRadius: 8, padding: isMobile ? 10 : 16, maxWidth: '92vw', width: isMobile ? '100%' : 680, maxHeight: '90vh', overflow: 'auto' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap' }}>
          <div style={{ color: '#e6e6e6', fontSize: isMobile ? 12 : 14, fontWeight: 700, display: 'flex', alignItems: 'center' }}>
            {(detail?.market || stock.market) && (
              <span style={{ color: '#7c8db0', fontSize: 11, marginRight: 6 }}>{(detail?.market || stock.market) === 'KOSPI' ? '코스피' : '코스닥'}</span>
            )}
            {stock.stockName} ({stock.stockCode})
            <button
              type="button"
              onClick={toggleFavorite}
              disabled={favLoading}
              style={{ background: 'none', border: 'none', padding: 0, marginLeft: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', color: isFavorite ? '#f59f00' : '#4a5a7a' }}
              title={isFavorite ? '즐겨찾기 해제' : '즐겨찾기 추가'}
            >
              {isFavorite ? <IconStarFilled size={16} /> : <IconStar size={16} />}
            </button>
          </div>
          <div className="d-flex gap-2">
            <a className="btn btn-sm btn-outline-success" href={`https://finance.naver.com/item/main.naver?code=${stock.stockCode}`}
              target="_blank" rel="noopener noreferrer">네이버증권</a>
            <button className="btn btn-sm btn-outline-secondary" onClick={onClose}>닫기</button>
          </div>
        </div>

        {loading && <div style={{ color: '#7c8db0', fontSize: 12 }}>불러오는 중...</div>}
        {!loading && !detail && <div style={{ color: '#7c8db0', fontSize: 12 }}>상세 데이터를 불러오지 못했습니다.</div>}

        {!loading && detail && (
          <div className="d-flex gap-4 flex-wrap">
            {detail.strategyScore != null && (
              <div style={{ flex: '1 1 100%', padding: '8px 10px', background: '#0f1a30', borderRadius: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                  <span style={{ color: '#e6e6e6', fontSize: 13, fontWeight: 700 }}>전략 적합도</span>
                  <ScoreBadge score={detail.strategyScore} />
                </div>
                <div className="d-flex flex-wrap gap-3" style={{ fontSize: 11, color: '#ccc', marginBottom: detail.reasonSummary ? 6 : 0 }}>
                  <div>저평가 <StarRating value={detail.valuationStars} /></div>
                  <div>외국인 수급 <StarRating value={detail.foreignStars} /></div>
                  <div>기관 수급 <StarRating value={detail.institutionStars} /></div>
                  <div>공매도 부담 <StarRating value={detail.shortStars} /></div>
                </div>
                {detail.reasonSummary && (
                  <div style={{ color: '#9fb0d0', fontSize: 11, lineHeight: 1.5 }}>{detail.reasonSummary}</div>
                )}
                <div style={{ color: '#5a6b8c', fontSize: 10, marginTop: 4 }}>
                  전략 적합도는 설정한 조건을 종합한 참고 지표이며 매수 추천을 의미하지 않습니다.
                </div>
              </div>
            )}

            <div style={{ flex: '1 1 280px', minWidth: 240 }}>
              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>기본정보</div>
              {row('기준일', detail.tradeDate)}
              {row('현재가', fmtWon(detail.closePrice))}
              {row('시가총액', fmtWonCap(detail.marketCap))}
              {row('거래량', fmt(detail.tradingVolume))}
              {(!detail.valuationDate || !detail.investorDate || !detail.shortDate) && (
                <div style={{ color: '#facc15', fontSize: 10, marginTop: 4 }}>
                  {!detail.valuationDate && '· 밸류에이션 데이터 없음 '}
                  {!detail.investorDate && '· 수급 데이터 없음 '}
                  {!detail.shortDate && '· 공매도 데이터 없음'}
                </div>
              )}

              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, margin: '10px 0 4px' }}>저평가 (Valuation)</div>
              {row('PBR', fmtPbr(detail.pbr))}
              {row('PER', fmtPbr(detail.per))}
              {row('EPS', fmtWon(detail.eps))}
              {row('BPS', fmtWon(detail.bps))}
              {row('DPS', fmtWon(detail.dps))}
              {row('배당수익률', fmtPct(detail.dividendYield))}

              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, margin: '10px 0 4px' }}>주가 추세</div>
              {row('5일 수익률', fmtPctSigned(detail.priceReturn5d))}
              {row('20일 수익률', fmtPctSigned(detail.priceReturn20d))}
              {row('60일 수익률', fmtPctSigned(detail.priceReturn60d))}
            </div>

            <div style={{ flex: '1 1 280px', minWidth: 240 }}>
              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, marginBottom: 4 }}>수급 (기준일 스냅샷)</div>
              {row('개인 순매수', fmtWonCapSigned(detail.individualNetBuyValue))}
              {row('외국인 순매수', fmtWonCapSigned(detail.foreignNetBuyValue))}
              {row('기관 순매수', fmtWonCapSigned(detail.institutionNetBuyValue))}

              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, margin: '10px 0 4px' }}>공매도 (기준일 스냅샷)</div>
              {row('공매도 거래량', fmt(detail.shortVolume))}
              {row('총 거래량', fmt(detail.totalVolume))}
              {row('거래비중', fmtPct(detail.shortVolumeRatio))}
              {row('잔고수량', fmt(detail.shortBalanceQuantity))}
              {row('잔고금액', fmtWonCap(detail.shortBalanceAmount))}
              {row('잔고비중', fmtPct(detail.shortBalanceRatio))}
            </div>

            <div style={{ flex: '1 1 100%' }}>
              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, margin: '6px 0 2px' }}>최근 수급 추이</div>
              <div style={{ fontSize: 10, color: '#7c8db0', marginBottom: 2 }}>
                <span style={{ color: '#e53e3e' }}>●</span> 외국인 &nbsp; <span style={{ color: '#3182ce' }}>●</span> 기관
              </div>
              <div style={{ fontSize: 11, color: '#ccc', display: 'flex', gap: 16, flexWrap: 'wrap', margin: '2px 0 6px' }}>
                <span>5일 합계 · 외국인 {fmtWonCapSigned(detail.foreignNetBuySum5d)} / 기관 {fmtWonCapSigned(detail.institutionNetBuySum5d)}</span>
                <span>20일 합계 · 외국인 {fmtWonCapSigned(detail.foreignNetBuySum20d)} / 기관 {fmtWonCapSigned(detail.institutionNetBuySum20d)}</span>
              </div>
              <TrendMiniChart
                series={detail.investorTrend}
                lines={[{ key: 'foreignNetBuyValue', color: '#e53e3e' }, { key: 'institutionNetBuyValue', color: '#3182ce' }]}
                fmtValue={fmtWonCap}
                isMobile={isMobile}
              />
              <div style={{ color: '#7c8db0', fontSize: 11, fontWeight: 700, margin: '10px 0 2px' }}>최근 공매도 추이</div>
              <div style={{ fontSize: 10, color: '#7c8db0', marginBottom: 2 }}>
                <span style={{ color: '#dd6b20' }}>●</span> 거래비중 &nbsp; <span style={{ color: '#9f7aea' }}>●</span> 잔고비중
              </div>
              <div style={{ fontSize: 11, color: '#ccc', display: 'flex', gap: 16, flexWrap: 'wrap', margin: '2px 0 6px' }}>
                <span>거래비중 · 5일평균 {fmtPct(detail.shortVolumeRatioAvg5d)} / 20일평균 {fmtPct(detail.shortVolumeRatioAvg20d)}</span>
                <span>잔고비중 · 5일평균 {fmtPct(detail.shortBalanceRatioAvg5d)} / 20일평균 {fmtPct(detail.shortBalanceRatioAvg20d)}</span>
              </div>
              <TrendMiniChart
                series={detail.shortSellingTrend}
                lines={[{ key: 'shortVolumeRatio', color: '#dd6b20' }, { key: 'shortBalanceRatio', color: '#9f7aea' }]}
                fmtValue={fmtPct}
                isMobile={isMobile}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── 최근 추이 미니 라인차트 (여러 시리즈, 데이터 없는 날은 선을 끊는다) ── */
function TrendMiniChart({ series, lines, fmtValue, isMobile }) {
  if (!series || series.length === 0) {
    return <div style={{ color: '#666', fontSize: 11, padding: '6px 0' }}>추이 데이터가 없습니다.</div>;
  }
  const W = isMobile ? 280 : 620;
  const H = 120;
  const PAD_L = 44, PAD_R = 10, PAD_T = 10, PAD_B = 20;
  const innerW = W - PAD_L - PAD_R, innerH = H - PAD_T - PAD_B;

  const allVals = series.flatMap(p => lines.map(l => p[l.key]).filter(v => v != null).map(Number));
  if (allVals.length === 0) {
    return <div style={{ color: '#666', fontSize: 11, padding: '6px 0' }}>추이 데이터가 없습니다.</div>;
  }
  const rawMin = Math.min(0, ...allVals), rawMax = Math.max(0, ...allVals);
  const range = rawMax - rawMin || 1;
  const yFor = (v) => PAD_T + ((rawMax - v) / range) * innerH;
  const xFor = (i) => PAD_L + (series.length <= 1 ? 0 : (i / (series.length - 1)) * innerW);
  const labelStep = Math.max(1, Math.ceil(series.length / 6));

  return (
    <svg width={W} height={H}>
      <line x1={PAD_L} y1={yFor(0)} x2={W - PAD_R} y2={yFor(0)} stroke="#2a3a5a" strokeDasharray="3,3" />
      <text x={PAD_L - 6} y={yFor(rawMax) + 4} textAnchor="end" fontSize={9} fill="#7c8db0">{fmtValue(rawMax)}</text>
      <text x={PAD_L - 6} y={yFor(rawMin) + 4} textAnchor="end" fontSize={9} fill="#7c8db0">{fmtValue(rawMin)}</text>
      {lines.map(l => {
        // 값이 null인 날짜는 선을 끊기 위해 연속 구간별로 별도 polyline을 그린다
        const segments = [];
        let current = [];
        series.forEach((p, i) => {
          if (p[l.key] == null) {
            if (current.length > 1) segments.push(current);
            current = [];
          } else {
            current.push(`${xFor(i).toFixed(1)},${yFor(Number(p[l.key])).toFixed(1)}`);
          }
        });
        if (current.length > 1) segments.push(current);
        return segments.map((seg, si) => (
          <polyline key={`${l.key}-${si}`} points={seg.join(' ')} fill="none" stroke={l.color} strokeWidth={1.6} />
        ));
      })}
      {series.map((p, i) => (
        (i % labelStep === 0 || i === series.length - 1) && (
          <text key={`x-${p.date}`} x={xFor(i)} y={H - PAD_B + 14} textAnchor="middle" fontSize={8} fill="#7c8db0">
            {String(p.date).slice(5)}
          </text>
        )
      ))}
    </svg>
  );
}

/* ── 시장분석 페이지 ────────────────────────────── */
export default function MarketAnalysisPage() {
  const [date, setDate] = useState(getKSTDate());
  const [reloadTick, setReloadTick] = useState(0);
  const [krxLoading, setKrxLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  /* [KRX] 버튼 - KRX Data Marketplace 전종목 시세(krx_stock_daily) + 투자자별 순매수상위종목(krx_investor_trading_daily)
     + PER/PBR/배당수익률(krx_stock_valuation_daily) + 공매도 거래/잔고(krx_short_selling_daily)를 순차 수집
     (재수집 시 기존 데이터는 DELETE 후 INSERT). 앞 단계가 실패하면 이후 단계는 호출하지 않는다.
     시세(1단계) 조회 결과가 KOSPI/KOSDAQ 모두 0건이면 해당 날짜는 영업일이 아닌 것으로 간주하고 이후 단계 없이 종료한다. */
  const handleKrxFetch = async () => {
    setKrxLoading(true);
    try {
      const savedCounts = await marketApi.collectKrxStockDaily(date);
      console.log('[KRX] stock-daily/collect response:', savedCounts);

      if (!savedCounts.KOSPI && !savedCounts.KOSDAQ) {
        alert(`${date}는 영업일이 아닌 것으로 보입니다 (시세 데이터 없음). 수집을 종료합니다.`);
        return;
      }

      const investorCounts = await marketApi.collectKrxInvestorTrading(date);
      console.log('[KRX] investor-trading/collect response:', investorCounts);

      const valuationCounts = await marketApi.collectKrxStockValuation(date);
      console.log('[KRX] stock-valuation/collect response:', valuationCounts);

      const shortSellingCounts = await marketApi.collectKrxShortSelling(date);
      console.log('[KRX] short-selling/collect response:', shortSellingCounts);

      alert(
        `KRX 저장 완료 (${date})\n`
        + `[시세] KOSPI ${savedCounts.KOSPI}종목 / KOSDAQ ${savedCounts.KOSDAQ}종목\n`
        + `[투자자별 순매수] ${Object.entries(investorCounts).map(([k, v]) => `${k} ${v}건`).join(', ')}\n`
        + `[PER/PBR/배당수익률] ${Object.entries(valuationCounts).map(([k, v]) => `${k} ${v}종목`).join(', ')}\n`
        + `[공매도] ${Object.entries(shortSellingCounts).map(([k, v]) => `${k} ${v}건`).join(', ')}`
      );
      setReloadTick(t => t + 1);
    } catch (e) {
      console.error('[KRX] 저장 실패:', e);
      alert(`KRX 저장 실패: ${e.response?.data?.message || e.message}`);
    } finally {
      setKrxLoading(false);
    }
  };

  /* [yyyy-mm-dd 삭제] 버튼 — 선택된 날짜의 4개 KRX 원본 테이블 데이터를 모두 삭제 ([KRX 데이터] 화면과 기능 동일) */
  const handleDeleteDate = async () => {
    if (!date) return;
    if (!window.confirm(`${date} 데이터를 삭제하시겠습니까?\n(시세/시총, 투자자 수급, 밸류에이션, 공매도 4개 테이블 전체)\n이 작업은 되돌릴 수 없습니다.`)) return;
    setDeleteLoading(true);
    try {
      const counts = await marketApi.deleteByDate(date);
      alert(
        `${date} 데이터 삭제 완료\n`
        + `[시세/시총] ${counts.stock}건\n`
        + `[투자자 수급] ${counts.investor}건\n`
        + `[밸류에이션] ${counts.valuation}건\n`
        + `[공매도] ${counts.shortSelling}건`
      );
      setReloadTick(t => t + 1);
    } catch (e) {
      alert(`삭제 실패: ${e.response?.data?.message || e.message}`);
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div className="container-xl py-4">
      <div className="page-header mb-3">
        <div className="row align-items-center">
          <div className="col">
            <div className="page-pretitle">자산</div>
            <h2 className="page-title d-flex align-items-center gap-2">
              <IconChartBar size={24} />
              시장분석
            </h2>
          </div>
          <div className="col-auto d-flex gap-2 align-items-center flex-wrap">
            <button className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1"
              onClick={handleKrxFetch} disabled={krxLoading}>
              <IconRefresh size={14} className={krxLoading ? 'rotating' : ''} />
              {krxLoading ? 'KRX 저장 중...' : 'KRX데이터수집'}
            </button>
            <button className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
              onClick={handleDeleteDate} disabled={!date || deleteLoading}>
              <IconTrash size={14} />
              {deleteLoading ? '삭제 중...' : `${date || ''} 삭제`}
            </button>
          </div>
        </div>
      </div>

      <MarketCapRankingBox date={date} setDate={setDate} reloadTick={reloadTick} />
      <ScreeningBox />
      <RankTrendAnalysisBox />
      <TrendAnalysisBox />
    </div>
  );
}
